// Where the Data model page's boxes go and how its lines run (data_map_controller.js). No DOM here, so
// the layout can be scored outside the browser too.
//
// Every area's rows sit on one grid across the whole board, so the gaps between rows run straight
// across it. A line leaves its box from the side facing the other end, runs up or down the gutter
// beside that column, along the row gap next to the other end, and up or down the gutter beside it
// into the box: it never crosses a box. Lines sharing a gutter or a gap each get a lane of their own.
export const W = 200, H = 54, PITCH = 88, HEAD = 40, PAD = 18, GAP_X = 56
const COLUMN_X = { sources: 0, jobs: 300 }, MODEL_X = 640, PAGES_GAP = 120, EDGE_GUTTER = 70
const LANE = 7, RADIUS = 8

export function layoutMap({ areas, nodes, edges }) {
  const areaMap = new Map(areas.map(a => [a.id, { ...a }]))
  const rows = id => Math.max(...nodes.filter(n => n.area === id).map(n => n.at[1])) + 1
  let modelRow = 0
  for (const a of areaMap.values()) {
    a.w = a.cols * W + (a.cols - 1) * GAP_X + 2 * PAD
    a.h = HEAD + rows(a.id) * PITCH - (PITCH - H) + PAD
    if (a.id in COLUMN_X) { a.x = COLUMN_X[a.id]; a.y = 0 }
    else if (a.id !== "pages") { a.x = MODEL_X; a.y = modelRow * PITCH; modelRow += rows(a.id) + 1 } // a row's room for the next one's heading
  }
  const models = [...areaMap.values()].find(a => a.x === MODEL_X)
  Object.assign(areaMap.get("pages"), { x: MODEL_X + models.w + PAGES_GAP, y: 0 })

  const nodeMap = new Map(nodes.map(n => {
    const a = areaMap.get(n.area)
    return [n.id, { ...n, x: a.x + PAD + n.at[0] * (W + GAP_X), y: a.y + HEAD + n.at[1] * PITCH }]
  }))
  const columns = [...new Set([...nodeMap.values()].map(n => n.x))].sort((a, b) => a - b)
  for (const n of nodeMap.values()) { n.col = columns.indexOf(n.x); n.row = Math.round((n.y - HEAD) / PITCH) }
  // Gutter g runs left of column g (the last one right of the last column).
  const gutters = columns.map((x, i) => ({ x0: i ? columns[i - 1] + W : x - EDGE_GUTTER, x1: x }))
  gutters.push({ x0: columns.at(-1) + W, x1: columns.at(-1) + W + EDGE_GUTTER })

  const all = [...areaMap.values()]
  const bounds = { w: Math.max(...all.map(a => a.x + a.w)), h: Math.max(...all.map(a => a.y + a.h)) }
  const kept = edges.filter(e => nodeMap.has(e.from) && nodeMap.has(e.to))
  return { areas: areaMap, nodes: nodeMap, edges: kept, bounds, routes: routeEdges(nodeMap, kept, gutters) }
}

// The gap under row k (between it and row k + 1).
const gapY = k => HEAD + k * PITCH + H + (PITCH - H) / 2

function routeEdges(nodes, edges, gutters) {
  const plans = edges.map(e => plan(nodes.get(e.from), nodes.get(e.to)))
  assignPorts(plans)
  const lanes = { v: assignLanes(plans, "v"), h: assignLanes(plans, "h") }
  return plans.map((p, i) => {
    const pts = points(p, gutters, lanes, i)
    return { points: pts, d: roundedPath(pts), mid: labelPoint(pts) }
  })
}

// Which sides, gutters and gap a line uses, before lanes and ports are worked out.
function plan(a, b) {
  if (a.col === b.col && Math.abs(a.row - b.row) === 1) return { a, b, straight: true }
  let sideA, sideB, gA, gB
  if (b.col > a.col) { sideA = "r"; sideB = "l"; gA = a.col + 1; gB = b.col }
  else if (b.col < a.col) { sideA = "l"; sideB = "r"; gA = a.col; gB = b.col + 1 }
  else { sideA = sideB = "r"; gA = gB = a.col + 1 }
  const p = { a, b, sideA, sideB, gA, gB }
  if (gA !== gB) p.gap = b.row >= a.row ? b.row - 1 : b.row // the gap beside b that faces a
  return p
}

// Lines on one side of a box leave it spread down that side, in the order of where they're going.
function assignPorts(plans) {
  const sides = new Map()
  const add = (node, side, p, end, toward) => {
    const key = `${node.id}:${side}`
    if (!sides.has(key)) sides.set(key, [])
    sides.get(key).push({ p, end, toward })
  }
  for (const p of plans) {
    if (p.straight) continue
    add(p.a, p.sideA, p, "a", p.gap != null ? gapY(p.gap) : p.b.y + H / 2)
    add(p.b, p.sideB, p, "b", p.gap != null ? gapY(p.gap) : p.a.y + H / 2)
  }
  for (const list of sides.values()) {
    const node = list[0].end === "a" ? list[0].p.a : list[0].p.b
    list.sort((x, y) => x.toward - y.toward)
    list.forEach((s, i) => { s.p[`port${s.end.toUpperCase()}`] = node.y + H * (i + 1) / (list.length + 1) })
  }
}

// Segments sharing a gutter (v) or a gap (h) that overlap get different lanes (interval colouring),
// ordered so lines heading the same way sit side by side.
function assignLanes(plans, dir) {
  const groups = new Map()
  plans.forEach((p, i) => {
    if (p.straight) return
    const segs = dir === "v" ? verticalSegments(p) : horizontalSegments(p)
    for (const s of segs) {
      if (!groups.has(s.key)) groups.set(s.key, [])
      groups.get(s.key).push({ ...s, i })
    }
  })
  const lanes = new Map()
  for (const [key, segs] of groups) {
    segs.sort((x, y) => x.lo - y.lo || x.hi - y.hi)
    const ends = []
    for (const s of segs) {
      let lane = ends.findIndex(end => end < s.lo - 4)
      if (lane < 0) { lane = ends.length; ends.push(0) }
      ends[lane] = s.hi
      lanes.set(`${key}#${s.i}#${s.part}`, lane)
    }
    lanes.set(`${key}#count`, ends.length)
  }
  return lanes
}

function verticalSegments(p) {
  const yA = p.portA, yB = p.portB
  if (p.gap == null) return [{ key: `g${p.gA}`, lo: Math.min(yA, yB), hi: Math.max(yA, yB), part: "a" }]
  const gy = gapY(p.gap)
  return [
    { key: `g${p.gA}`, lo: Math.min(yA, gy), hi: Math.max(yA, gy), part: "a" },
    { key: `g${p.gB}`, lo: Math.min(gy, yB), hi: Math.max(gy, yB), part: "b" }
  ]
}

function horizontalSegments(p) {
  if (p.gap == null) return []
  return [{ key: `r${p.gap}`, lo: Math.min(p.gA, p.gB), hi: Math.max(p.gA, p.gB), part: "h" }]
}

function laneOffset(lanes, key, i, part, room) {
  const n = lanes.get(`${key}#count`) || 1
  const step = Math.min(LANE, (room - 12) / n)
  return (lanes.get(`${key}#${i}#${part}`) - (n - 1) / 2) * step
}

function points(p, gutters, lanes, i) {
  const { a, b } = p
  if (p.straight) {
    const down = b.row > a.row
    return [{ x: a.x + W / 2, y: down ? a.y + H : a.y }, { x: b.x + W / 2, y: down ? b.y : b.y + H }]
  }
  const sx = (n, side) => side === "r" ? n.x + W : n.x
  const gx = (g, part) => {
    const gut = gutters[g]
    return (gut.x0 + gut.x1) / 2 + laneOffset(lanes.v, `g${g}`, i, part, gut.x1 - gut.x0)
  }
  const start = { x: sx(a, p.sideA), y: p.portA }, end = { x: sx(b, p.sideB), y: p.portB }
  if (p.gap == null) {
    const x = gx(p.gA, "a")
    return [start, { x, y: start.y }, { x, y: end.y }, end]
  }
  const y = gapY(p.gap) + laneOffset(lanes.h, `r${p.gap}`, i, "h", PITCH - H)
  const xA = gx(p.gA, "a"), xB = gx(p.gB, "b")
  return [start, { x: xA, y: start.y }, { x: xA, y }, { x: xB, y }, { x: xB, y: end.y }, end]
}

// Straight runs with rounded corners.
function roundedPath(pts) {
  const clean = pts.filter((pt, i) => i === 0 || Math.hypot(pt.x - pts[i - 1].x, pt.y - pts[i - 1].y) > 0.5)
  let d = `M${clean[0].x},${clean[0].y}`
  for (let i = 1; i < clean.length - 1; i++) {
    const [p0, p1, p2] = [clean[i - 1], clean[i], clean[i + 1]]
    const r = Math.min(RADIUS, Math.hypot(p1.x - p0.x, p1.y - p0.y) / 2, Math.hypot(p2.x - p1.x, p2.y - p1.y) / 2)
    const inn = toward(p1, p0, r), out = toward(p1, p2, r)
    d += ` L${inn.x},${inn.y} Q${p1.x},${p1.y} ${out.x},${out.y}`
  }
  const last = clean.at(-1)
  return `${d} L${last.x},${last.y}`
}

function toward(from, to, r) {
  const len = Math.hypot(to.x - from.x, to.y - from.y) || 1
  return { x: from.x + (to.x - from.x) * r / len, y: from.y + (to.y - from.y) * r / len }
}

// A line's label goes on its longest straight run.
function labelPoint(pts) {
  let best = 0, at = { x: pts[0].x, y: pts[0].y }
  for (let i = 1; i < pts.length; i++) {
    const len = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y)
    if (len > best) { best = len; at = { x: (pts[i].x + pts[i - 1].x) / 2, y: (pts[i].y + pts[i - 1].y) / 2 } }
  }
  return at
}
